import { useCallback, useEffect, useRef, useState } from "react";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../__generated__/gql";
import { ArticleStatus } from "../../__generated__/gql/graphql";
import StatusBadge, {
  StatusBadgeFragment,
} from "../../components/StatusBadge/StatusBadge";
import EmptyState from "../../ui/EmptyState/EmptyState";
import { PlayCircle, Zap } from "../../ui/Icon/Icon";
import styles from "./ArticleEditor.module.scss";
import ArticleContent from "./components/ArticleContent/ArticleContent";
import ArticleHeader from "./components/ArticleHeader/ArticleHeader";
import { useMutation } from "urql";
import { useBeforeUnload } from "react-router-dom";
import ArticleFeedbackFooter from "../../pages/Article/ArticleFeedbackFooter";
import ArticleFeedbackCard from "../../pages/Article/ArticleFeedbackCard";

function ArticleEmptyState({
  title,
  status,
  onClick,
  data,
}: {
  title?: string | null;
  status: ArticleStatus;
  processingMessage?: string;
  onClick: () => void;
  data: FragmentType<typeof StatusBadgeFragment>;
}) {
  if (status === ArticleStatus.Processing) {
    return (
      <EmptyState
        title={title}
        icon={<Zap size="large" />}
        buttonText="Generate Article"
      >
        <StatusBadge data={data} />
      </EmptyState>
    );
  }

  return (
    <EmptyState
      title={title}
      icon={<Zap size="large" />}
      buttonText="Generate article"
      buttonIcon={<PlayCircle size="medium" />}
      buttonOnClick={onClick}
    />
  );
}

const ArticleEditorFragment = graphql(/* GraphQL */ `
  fragment ArticleEditor on Article {
    id
    title
    excerpt
    metaDescription
    processingMessage
    status
    feedbackQuality
    feedbackComment
    showFloatingFeedback
    heroImage {
      ...ArticleImage
    }
    sections {
      ...ArticleSection
    }
    tag {
      id
      name
    }
    ...StatusBadge
  }
`);

const UpdateArticleContentMutation = graphql(/* GraphQL */ `
  mutation updateArticleContent($input: UpdateArticleContentInput!) {
    updateArticleContent(input: $input) {
      id
    }
  }
`);

function ArticleEditor({
  data,
  openGenerateArticleModal,
  setUnsavedChanges,
}: {
  data: FragmentType<typeof ArticleEditorFragment>;
  openGenerateArticleModal: () => void;
  setUnsavedChanges: (unsavedChanges: boolean) => void;
}) {
  const article = getFragmentData(ArticleEditorFragment, data);
  const [, updateArticleContent] = useMutation(UpdateArticleContentMutation);
  const [content, setContent] = useState<{
    title?: string;
    excerpt?: string;
    sections: {
      id: string;
      title?: string;
      body: string;
    }[];
  }>({ sections: [] });
  const contentRef = useRef(content);
  const firstRender = useRef(true);
  const ref = useRef<HTMLDivElement>(null);
  const mainDiv = ref.current;
  const [scrollPercentage, setScrollPercentage] = useState(0);

  const onSave = useCallback(async () => {
    if (content && article.id) {
      await updateArticleContent({
        input: {
          id: article.id,
          title: content.title,
          excerpt: content.excerpt,
          sections: content.sections,
        },
      });
    }

    setUnsavedChanges(false);
  }, [article.id, content, setUnsavedChanges, updateArticleContent]);

  const saveArticleContent = useCallback(
    ({
      sections,
      title,
      excerpt,
    }: {
      title?: string;
      excerpt?: string;
      sections: {
        id: string;
        title?: string;
        body: string;
      }[];
    }) => {
      if (!article.id) {
        return;
      }

      setContent({ sections, title, excerpt });
    },
    [article.id],
  );

  // Save the article when the user closes the page/tab
  useBeforeUnload(onSave);

  useEffect(() => {
    function getScrollParent(node: HTMLElement | null) {
      if (node == null) {
        return null;
      }

      if (node.scrollHeight > node.clientHeight) {
        return node;
      } else {
        return getScrollParent(node.parentNode as HTMLElement);
      }
    }

    const scrollParent = getScrollParent(mainDiv);

    if (scrollParent) {
      const handleScroll = () => {
        if (scrollParent) {
          const { scrollHeight, scrollTop, clientHeight } = scrollParent;

          setScrollPercentage(scrollTop / (scrollHeight - clientHeight));
        }
      };

      scrollParent.addEventListener("scroll", handleScroll);

      return () => {
        scrollParent.removeEventListener("scroll", handleScroll);
      };
    }
  }, [mainDiv]);

  useEffect(() => {
    contentRef.current = content;

    if (firstRender.current) {
      firstRender.current = false;

      return;
    }

    const timeout = setTimeout(onSave, 2_500);

    setUnsavedChanges(true);

    return () => clearTimeout(timeout);
  }, [content, onSave, setUnsavedChanges, updateArticleContent]);

  if (
    article.title &&
    article.sections &&
    article.excerpt &&
    article.metaDescription &&
    article.status !== ArticleStatus.Error &&
    (article.status !== ArticleStatus.Processing ||
      !!article.processingMessage?.startsWith("Regenerating section"))
  ) {
    return (
      <div className={styles.pageLeftInner} id="article" ref={ref}>
        <ArticleHeader
          articleId={article.id}
          tag={article.tag?.name}
          metaDescription={article.metaDescription}
          data={article.heroImage}
        />
        <ArticleContent
          title={article.title}
          excerpt={article.excerpt}
          articleId={article.id}
          sections={article.sections}
          onUpdate={saveArticleContent}
        />

        <ArticleFeedbackFooter
          id={article.id}
          isDismissed={
            !!(
              article.feedbackQuality ||
              article.feedbackComment ||
              article.showFloatingFeedback
            )
          }
        />

        <ArticleFeedbackCard
          id={article.id}
          articleScrollPercentage={scrollPercentage}
          isDismissed={!article.showFloatingFeedback}
        />
      </div>
    );
  }

  return (
    <ArticleEmptyState
      title={article.title}
      status={article.status}
      data={article}
      onClick={openGenerateArticleModal}
    />
  );
}

export default ArticleEditor;
